import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Skeleton,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { DeleteBtn, SearchBtn } from '../../Buttons/Buttons';
import * as HeaderAuthentication from '../../../components/HeaderAuthentication'
import { postDeleteViewedContactsData, postPhoneUnlockDatas, unlockAllViewedContacts } from '../../redux/action';
import { useDispatch } from 'react-redux';
import { Circles } from 'react-loader-spinner';
import noDataGif from '../../../assets/images/No data.gif'
dayjs.extend(relativeTime);

export default function ViewedContactsTable({
    page,
    setPage,
    rowsPerPage,
    totalDataCount,
    loading,
    fetchViewedContact,
    loadingMore,
    setLoadingMore,
    tableHeight,
    selectedRowsData,
    setSelectedRowsData,
    getViewedContacts,
    div2Ref,
    handleDownload,
    signInID,
    sbnID,
    role
}) {

    const dispatch = useDispatch();
    const signInDetails = JSON.parse(sessionStorage.getItem("SignInDetails"));

    const [pageInput, setPageInput] = React.useState('');
    const [selectedRowIds, setSelectedRowIds] = React.useState([]);
    const [resetKey, setResetKey] = React.useState(0);

    const handlePhoneUnlock = (uiRowId) => {
        const row = paginatedRows.find(r => r.id === uiRowId);
        if (!row) return;
        const requestPayload = {
            user_id: parseInt(signInDetails[0]?.id),
            user_name: signInDetails[0]?.name,
            sbn_id: signInDetails[0]?.sbn_id,
            list_name: "Default",
            contacts: [{ contact_id: parseInt(row.apiId), phone: true }],
        };

        const requestOption = {
            method: "POST",
            mode: "cors",
            headers: HeaderAuthentication.postAuthOfLeadMerge,
            body: JSON.stringify(requestPayload),
        };
        
        dispatch(postPhoneUnlockDatas(requestOption, 'phone', fetchViewedContact));
    };

    const handleEmailUnlock = (uiRowId) => {
        const row = paginatedRows.find(r => r.id === uiRowId);
        if (!row) return;
        const requestPayload = {
            user_id: parseInt(signInDetails[0]?.id),
            user_name: signInDetails[0]?.name,
            sbn_id: signInDetails[0]?.sbn_id,
            list_name: "Default",
            contacts: [{ contact_id: parseInt(row.apiId), email: true }],
        };

        const requestOption = {
            method: "POST",
            mode: "cors",
            headers: HeaderAuthentication.postAuthOfLeadMerge,
            body: JSON.stringify(requestPayload),
        };
        
        dispatch(postPhoneUnlockDatas(requestOption, 'email', fetchViewedContact));
    };

    
    const renderSkeletonCell = (params) => {
      if (params.row?.isSkeleton) {
        return <Skeleton variant="text" width="100%" />;
      }
    
      if (params.field === 'phone') {
        return params?.row?.phone === '-' ? (
          <span
            style={{
              color: '#007bff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => handlePhoneUnlock(params.row.id)}
          >
            <i className="fa fa-phone" style={{ marginRight: 6 }}></i>
            Unlock
          </span>
        ) : (
          params?.value || 'NA'
        );
      }
    
      if (params.field === 'email') {
        return params?.row?.email === '-' ? (
          <span
            style={{
              color: '#007bff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => handleEmailUnlock(params.row.id)}
          >
            <i className="fa fa-envelope" style={{ marginRight: 6 }}></i>
            Unlock
          </span>
        ) : (
          params?.value || 'NA'
        );
      }
    
      return params.value || 'NA';
    };

    // Define DataGrid columns
    const columns = [
        { field: 'id', headerName: 'ID', width: 70, renderCell: renderSkeletonCell },
        { field: 'firstName', headerName: 'First Name', width: 130, renderCell: renderSkeletonCell },
        { field: 'lastName', headerName: 'Last Name', width: 130, renderCell: renderSkeletonCell },
        { field: 'jobLevel', headerName: 'Job Level', width: 180, renderCell: renderSkeletonCell },
        { field: 'jobDepartment', headerName: 'Job Department', width: 180, renderCell: renderSkeletonCell },
        { field: 'title', headerName: 'Title', width: 220, renderCell: renderSkeletonCell },
        { field: 'company', headerName: 'Company', width: 200, renderCell: renderSkeletonCell },
        { field: 'website', headerName: 'Website', width: 180, renderCell: renderSkeletonCell },
        {
            field: 'phone',
            headerName: 'Phone',
            width: 130,
            renderCell: renderSkeletonCell,
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 200,
            renderCell: renderSkeletonCell,
        },
        { field: 'industry', headerName: 'Industry', width: 180, renderCell: renderSkeletonCell },
        { field: 'subIndustry', headerName: 'SubIndustry', width: 250, renderCell: renderSkeletonCell },
        { field: 'technology', headerName: 'Technology', width: 150, renderCell: renderSkeletonCell },
        { field: 'city', headerName: 'City', width: 130, renderCell: renderSkeletonCell },
        { field: 'state', headerName: 'State', width: 130, renderCell: renderSkeletonCell },
        { field: 'country', headerName: 'Country', width: 130, renderCell: renderSkeletonCell },
        { field: 'zipCode', headerName: 'Zip Code', width: 130, renderCell: renderSkeletonCell },
        { field: 'sicCode', headerName: 'Sic Code', width: 130, renderCell: renderSkeletonCell },
        { field: 'revenue', headerName: 'Revenue', width: 150, renderCell: renderSkeletonCell },
        { field: 'employee', headerName: 'Employee', width: 150, renderCell: renderSkeletonCell },
    ];

    // Helper to map API response to DataGrid row format
    const transformSearchContactData = (data) => {
        return data.map((item, index) => ({
            // id: (index + 1) + ((page - 1) * rowsPerPage),
            id: (index + 1),
            apiId: item.id,
            firstName: item.firstname || 'NA',
            lastName: item.lastname || 'NA',
            jobLevel: item.job_level || 'NA',
            jobDepartment: item.job_department || 'NA',
            title: item.job_title || 'NA',
            company: item.company || 'NA',
            website: item.website || 'NA',
            phone: item.phone || 'NA',
            email: item.email || 'NA',
            industry: item.industry || 'NA',
            subIndustry: item.sub_industry || 'NA',
            technology: item.technology || 'NA',
            city: item.city || 'NA',
            state: item.state || 'NA',
            country: item.country || 'NA',
            zipCode: item.zipcode || 'NA',
            sicCode: item.sic_code || 'NA',
            revenue: item.revenue || 'NA',
            employee: item.employee_size || 'NA',
            list_name: item.list_name,
        }));
    };

    let rows = [];

    if (loading) {
        // show skeleton rows
        rows = Array.from({ length: 10 }).map((_, index) => ({
            id: index + 1,
            apiId: null,
            firstName: '',
            lastName: '',
            jobLevel: '',
            jobDepartment: '',
            title: '',
            company: '',
            website: '',
            phone: '',
            email: '',
            industry: '',
            subIndustry: '',
            technology: '',
            city: '',
            state: '',
            country: '',
            zipCode: '',
            sicCode: '',
            revenue: '',
            employee: '',
            list_name: '',
            isSkeleton: true,
        }));
    } else {
        // only transform if getViewedContacts is an array
        rows = Array.isArray(getViewedContacts)
            ? transformSearchContactData(getViewedContacts)
            : [];
    }

    // Calculate total number of pages using total data count from API
    const totalPages = Math.ceil(totalDataCount / rowsPerPage);

    // Backend already returns paginated data, so no need to slice
    const paginatedRows = rows;

    // Handle pagination button change
    const handlePageChange = (event, value) => {
        setPage(value);
        setPageInput('');
    };

    // Manual "Go" to page
    const handleGoToPage = () => {
        const pageNumber = parseInt(pageInput, 10);
        if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
            setPage(pageNumber);
        }
    };

    const scrollContainerRef = React.useRef(null);

    React.useEffect(() => {
        const container = document.querySelector('.MuiDataGrid-virtualScroller');
        if (!container) return;

        let lastScrollTop = 0;

        const handleScroll = () => {
            if (!container || loadingMore) return;

            const currentScrollTop = container.scrollTop;

            if (currentScrollTop <= lastScrollTop) return;

            lastScrollTop = currentScrollTop;

            const distanceFromBottom = container.scrollHeight - currentScrollTop - container.clientHeight;

            if (distanceFromBottom < 10 && getViewedContacts.length < totalDataCount && !loadingMore) {
                setLoadingMore(true);
                fetchViewedContact();
                setPage((prev) => prev + 1);
            }
        };

        container.addEventListener('scroll', handleScroll);

        // Cleanup function
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [loadingMore, getViewedContacts.length, totalDataCount, setPage]);
    // console.log(tableHeight, 'tableHeight')

    const handleDeleteViewedData = () => {
        if (!selectedRowIds.length) return;

        const contacts = selectedRowIds
            .map((id) => {
                const row = paginatedRows.find((r) => r.id === id);
                return row?.apiId ? {
                    contact_id: parseInt(row.apiId),
                    list_name: row.list_name,
                } : null;
            })
            .filter(Boolean);

        if (!contacts.length) return;

        const clearSelection = () => {
            setSelectedRowIds([]);        // unchecks all checkboxes
            setSelectedRowsData([]);      // clears selected row data
            setResetKey(prev => prev + 1);  // forces DataGrid to re-render clean
        };

        const requestPayload = {
            user_id: parseInt(signInDetails[0]?.id),
            user_name: signInDetails[0]?.name,
            sbn_id: signInDetails[0]?.sbn_id,
            contacts,
        };

        const requestOption = {
            method: "POST",
            mode: "cors",
            headers: HeaderAuthentication.postAuthOfLeadMerge,
            body: JSON.stringify(requestPayload),
        };

        // console.log(requestPayload, 'requestPayload');
        dispatch(postDeleteViewedContactsData(requestOption, fetchViewedContact, clearSelection));
    };

    const CustomNoRowsOverlay = () => (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            p={2}
        >
            <img src={noDataGif} alt="No Data" width={180} style={{ marginBottom: 10 }} />
            <Typography variant="subtitle1" color="textSecondary">
                No Data Available
            </Typography>
        </Box>
    );

    const handleUnlockAllContacts = () => {
        dispatch(unlockAllViewedContacts(signInID, sbnID, 'Default', role, fetchViewedContact))
    }

    return (
        <>
            <div className='listname-count-div' ref={div2Ref}>
                <p>List Name- Contacts &nbsp;(<span>{totalDataCount}</span>)</p>
                <div className='view-contacts-btn'>
                    <SearchBtn btnName="Unlock All" onClickEffect={handleUnlockAllContacts} />
                    <DeleteBtn btnName="Delete From List" onClickEffect={handleDeleteViewedData} />
                    <SearchBtn btnName="Download" onClickEffect={handleDownload} />
                </div>
            </div>
            <div style={{ position: 'relative' }}>
                <Box sx={{ width: '100%', mt: 2 }}>
                    {/* <Paper sx={{ height: 300, width: '100%' }}> */}
                    <DataGrid
                        key={resetKey}
                        sx={{ height: `${tableHeight}px !important` }}
                        ref={scrollContainerRef}
                        // autoHeight
                        pagination={false}
                        rowCount={totalDataCount}
                        paginationMode="server"
                        rows={paginatedRows}
                        columns={columns}
                        hideFooter
                        checkboxSelection
                        className="search-contact-data-grid"
                        getRowId={(row) => row.id}
                        onRowClick={(row) => row.id}
                        getRowClassName={(params) =>
                            params.row?.isSkeleton ? 'skeleton-row' : ''
                        }
                        onRowSelectionModelChange={(newSelection) => {
                            const selectedIds = Array.from(newSelection?.ids ?? []);
                            setSelectedRowIds(selectedIds);
                            const selectedData = paginatedRows?.filter((row) => selectedIds.includes(row.id));
                            setSelectedRowsData(selectedData);
                        }}
                        selectionModel={selectedRowIds}
                        loading={loading}
                        components={{
                            NoRowsOverlay: CustomNoRowsOverlay, // shows GIF only when loading is false and no rows
                        }}
                    />
                    {loadingMore && <div className='loader-css'>
                        <div className="d-flex justify-content-center align-items-center py-4">
                            <Circles
                                height="30"
                                width="30"
                                color="#007bff"
                                ariaLabel="circles-loading"
                                visible={true}
                            />
                        </div>
                    </div>}
                    
                    {/* </Paper> */}
                </Box>
            </div>
        </>
    );
}

